import { useEffect, useRef } from 'react'
import { Link } from 'react-router'
import { VisuallyHidden } from '@n26/nxd'
import type {
  CardStub,
  DashboardSection,
} from '@/features/dashboard/api/schemas'
import { toFigure } from '@/features/dashboard/format/figure'
import { useFilteredCard } from '@/features/dashboard/filters/useFilteredCard'
import { sentenceCase } from '@/features/dashboard/format/format'
import { deepDivePath } from '@/shared/routes'
import { ArrowRightIcon, CloseIcon } from '@/shared/ui/icons'
import {
  cardTransitionName,
  viewTransitionStyle,
} from '@/shared/lib/viewTransition'
import { CardFigure } from './CardFigure'
import { EmptyCardMessage } from './EmptyCardMessage'
import { ExpandedBarChart } from '../charts/ExpandedBarChart'
import { InfoTip } from './InfoTip'
import styles from './ExpandedModule.module.css'

type ExpandedModuleProps = {
  stub: CardStub
  deepDive: DashboardSection | undefined
  /** Whether to move focus into the panel (it was opened from the keyboard). */
  takeFocus: boolean
  /** `fromKeyboard`: the ✕ was pressed with a key rather than clicked. */
  onClose: (fromKeyboard: boolean) => void
}

/** A tile expanded into the panel beside the sidebar: its figure and chart, large. */
export const ExpandedModule = ({
  stub,
  deepDive,
  takeFocus,
  onClose,
}: ExpandedModuleProps) => {
  const {
    data: card,
    isPending,
    isError,
    isRefreshing,
    clearMarkets,
  } = useFilteredCard(stub.cardId)
  const closeRef = useRef<HTMLButtonElement>(null)

  // Keyboard users land on the panel they just opened. Mouse users don't, as
  // focus inside the card would show its hover-only controls.
  useEffect(() => {
    if (takeFocus) closeRef.current?.focus({ preventScroll: true })
  }, [takeFocus])

  const figure = card ? toFigure(card) : null

  return (
    <article
      className={`${styles.card} ${deepDive ? styles.hasDive : ''} ${isRefreshing ? styles.busy : ''}`}
      aria-busy={isRefreshing || undefined}
      aria-labelledby={`expanded-${stub.cardId}`}
      style={viewTransitionStyle(cardTransitionName(stub.cardId))}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 id={`expanded-${stub.cardId}`} className={styles.title}>
            {stub.name}
          </h3>
          {stub.description && (
            <InfoTip
              name={stub.name}
              description={stub.description}
              className={styles.info}
            />
          )}
        </div>
        <button
          ref={closeRef}
          type='button'
          className={styles.close}
          aria-label='Close expanded view'
          // A key press clicks with a detail of 0.
          onClick={event => onClose(event.detail === 0)}
        >
          <CloseIcon />
        </button>
      </div>
      {card?.kind === 'chart' && card.period && (
        <p className={styles.subtitle}>
          Month over month, {card.period.label.toLowerCase()}.
        </p>
      )}
      {card?.kind === 'empty' && (
        <EmptyCardMessage
          reason={card.reason}
          onClear={clearMarkets}
          className={styles.empty}
        />
      )}
      {isPending && (
        <div className={styles.skeleton} role='status'>
          <VisuallyHidden>Loading</VisuallyHidden>
        </div>
      )}
      {isError && (
        <p className={styles.error} role='alert'>
          Couldn&apos;t load this card.
        </p>
      )}
      {figure && (
        <CardFigure figure={figure} size='expanded' className={styles.values}>
          {deepDive && (
            <Link to={deepDivePath(deepDive.id)} className={styles.dive}>
              {sentenceCase(deepDive.title)}
              <ArrowRightIcon />
            </Link>
          )}
        </CardFigure>
      )}
      {card?.kind === 'chart' && card.categories.length > 0 && (
        <ExpandedBarChart card={card} />
      )}
    </article>
  )
}
